import React, { useState } from 'react';
import FormField from './FormField';
import FieldGroup from './FieldGroup';

const FormSection = ({ section, formData, onChange, errors = {}, visibleFieldIds }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { title, fields } = section;

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  const visibleFields = visibleFieldIds
    ? fields.filter(f => visibleFieldIds.has(f.id))
    : fields;

  const filledFields = visibleFields.filter(field => {
    const value = formData[field.id];
    return value && value.toString().trim() !== '';
  }).length;

  const progressPercentage = visibleFields.length > 0 ? Math.round((filledFields / visibleFields.length) * 100) : 0;

  const renderFields = () => {
    const rendered = [];
    const processedGroups = new Set();

    for (const field of visibleFields) {
      if (field.groupId && !processedGroups.has(field.groupId)) {
        processedGroups.add(field.groupId);
        const groupFields = visibleFields.filter(f => f.groupId === field.groupId);
        const groupMeta = section.fieldGroups?.find(g => g.groupId === field.groupId);
        rendered.push(
          <FieldGroup
            key={`group-${field.groupId}`}
            group={groupMeta || { groupId: field.groupId, label: null, layout: 'row' }}
            fields={groupFields}
            formData={formData}
            onChange={onChange}
            errors={errors}
          />
        );
      } else if (!field.groupId) {
        rendered.push(
          <FormField
            key={field.id}
            field={field}
            value={formData[field.id]}
            onChange={onChange}
            error={errors[field.id]}
          />
        );
      }
    }

    return rendered;
  };

  if (visibleFields.length === 0) return null;

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      overflow: 'hidden'
    }}>
      {/* Section Header */}
      <div
        style={{
          padding: '1.5rem',
          borderBottom: isCollapsed ? 'none' : '1px solid #e5e7eb',
          cursor: 'pointer',
          backgroundColor: isCollapsed ? '#f9fafb' : 'white'
        }}
        onClick={toggleCollapsed}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleCollapsed();
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#111827',
              margin: 0,
              marginBottom: '0.5rem'
            }}>
              {title}
            </h3>

            {/* Progress indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                flex: 1,
                backgroundColor: '#e5e7eb',
                borderRadius: '9999px',
                height: '0.25rem',
                overflow: 'hidden'
              }}>
                <div
                  style={{
                    backgroundColor: progressPercentage === 100 ? '#10b981' : '#2563eb',
                    height: '100%',
                    borderRadius: '9999px',
                    transition: 'width 0.3s',
                    width: `${progressPercentage}%`
                  }}
                />
              </div>
              <span style={{
                fontSize: '0.75rem',
                color: progressPercentage === 100 ? '#059669' : '#6b7280',
                fontWeight: '500',
                minWidth: '3rem'
              }}>
                {filledFields}/{visibleFields.length}
              </span>
            </div>
          </div>

          <svg
            style={{
              width: '1.25rem',
              height: '1.25rem',
              color: '#6b7280',
              transition: 'transform 0.2s',
              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
            }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div style={{ padding: '1.5rem' }}>
          {renderFields()}
        </div>
      )}
    </div>
  );
};

export default FormSection;
